import { useMemo } from 'react';

import { PeerParticipantsWith__Type, useListUserParticipants } from '@givebacks/api/store';
import { useBreakPoints } from 'core';
import { useNavigate } from 'react-router-dom';
import { CampaignStoreItemTypes } from 'store/Api';
import { PeerDonationCard } from 'store/Crowdfunding';
import { Card, Carousel, Group, Stack, Text, Title } from 'ui';
import { IconChevronLeft, IconChevronRight } from 'ui/Icons';

import { MyFundraisingPagesHistoryModal } from './MyFundraisingPagesHistoryModal';
import classes from './MyFundraisingPagesSection.module.css';

const MyFundraisingPagesSection = () => {
  const navigate = useNavigate();
  const { isSmallScreen } = useBreakPoints();
  const { data: participants } = useListUserParticipants({
    queryOptions: {
      searches: {
        'store_items.status': { value: 'published' },
      },
    },
  });

  const { data: finishedParticipants } = useListUserParticipants({
    queryOptions: {
      searches: {
        'store_items.status': { value: 'finished,finishing_required', by: 'in' },
      },
    },
  });

  const showFundraisingPages = useMemo(() => {
    return participants?.length || finishedParticipants?.length;
  }, [participants?.length, finishedParticipants?.length]);

  const hasOnlyFinishedParticipants = useMemo(() => {
    return finishedParticipants?.length && !participants?.length;
  }, [participants?.length, finishedParticipants?.length]);

  const showFundraisingHistoryModal = useMemo(() => {
    return finishedParticipants?.length;
  }, [finishedParticipants?.length]);

  const isShowIndicators = useMemo(() => {
    if (!participants?.length) return;
    return isSmallScreen || participants.length > 2;
  }, [isSmallScreen, participants?.length]);

  const isShowControls = useMemo(() => {
    if (!participants?.length) return;
    return !isSmallScreen && participants.length > 2;
  }, [isSmallScreen, participants?.length]);

  if (!showFundraisingPages) return null;

  return (
    <Stack gap={isSmallScreen ? 'md' : 'lg'}>
      <Group justify="space-between" wrap="nowrap">
        <Title order={2}>Fundraising {(participants?.length ?? 0) > 1 ? 'Pages' : 'Page'}</Title>
        {showFundraisingHistoryModal && <MyFundraisingPagesHistoryModal />}
      </Group>
      {hasOnlyFinishedParticipants ? (
        <Card bd="1px solid gray.1" bg="gray.0">
          <Text c="gray.6" fz="xs" fw={600}>
            You have no active fundraising pages.
          </Text>
        </Card>
      ) : (
        <Carousel
          slideSize={{ base: '100%', sm: '50%' }}
          slideGap="sm"
          align="start"
          withControls={isShowControls}
          withIndicators={isShowIndicators}
          draggable={isSmallScreen}
          nextControlIcon={<IconChevronRight />}
          previousControlIcon={<IconChevronLeft />}
          classNames={classes}
        >
          {participants?.map((participant) => (
            <Carousel.Slide key={participant.uuid}>
              <PeerDonationCard
                campaignPeerInfoCardProps={{
                  showCampaignName: true,
                  showTeamName: false,
                  showCampaignTimeLeft: true,
                  wrapDescription: false,
                }}
                onClick={() => {
                  navigate(
                    `/causes/${participant.store_item.cause_id}/campaigns/${participant.store_item.uuid}/participant-center/participants/${participant.uuid}`
                  );
                }}
                heightAuto
                type="participants"
                peer={
                  {
                    uuid: participant.uuid,
                    amount_raised: participant.amount_raised,
                    activity: participant.activity,
                    goal: participant.goal,
                  } as PeerParticipantsWith__Type
                }
                campaign={
                  {
                    ...participant.store_item,
                    __type:
                      participant.store_item.type === 'StoreItems::Athon' ? 'athon' : 'campaign',
                  } as CampaignStoreItemTypes
                }
              />
            </Carousel.Slide>
          ))}
        </Carousel>
      )}
    </Stack>
  );
};

export { MyFundraisingPagesSection };
